'use client';

/**
 * AiLoaderIcon is the AI loading mark. The centre dwells in turn on the three brand shapes (sparkle,
 * heart, b-mark), morphing between them, while two smaller sparkles orbit it at different speeds and
 * twinkle out of phase. The centre is filled by a repeating gradient sweep that loops seamlessly.
 *
 * The morph and the sweep run as SMIL inside the SVG; the orbits and the twinkle are CSS, timed by
 * custom properties so `speed` scales both halves together. Reduced motion swaps everything for a
 * still b-mark with the sparkles parked.
 */

import { useId, type CSSProperties } from 'react';

import { cn } from '../../../lib/utils/cn';
import { MORPH_BB_HEART, MORPH_BB_LOGO, MORPH_BB_SPARKLE } from './ai-loader-icon-paths';
import styles from './ai-loader-icon.module.css';

// Each shape repeats at two adjacent keyTimes so it HOLDS before morphing on.
const FRAMES = [
  MORPH_BB_SPARKLE,
  MORPH_BB_SPARKLE,
  MORPH_BB_HEART,
  MORPH_BB_HEART,
  MORPH_BB_LOGO,
  MORPH_BB_LOGO,
  MORPH_BB_SPARKLE,
];
const KEYTIMES = '0;0.2;0.33;0.53;0.66;0.86;1';

/*
  Per-segment easing. The three travelling segments use a front-loaded curve: most of the shape
  change happens early, then it decelerates into the dwell, which reads as a deliberate settle
  rather than a constant crossfade. SMIL keySplines control points must stay inside [0,1], so the
  overshoot a spring would use is not available here. Dwell segments hold identical frames, so their
  curve is irrelevant and keeps the neutral one.
*/
const DWELL_SPLINE = '0.42 0 0.58 1';
const MORPH_SPLINE = '0.6 0 0.2 1';
const SPLINES = [
  DWELL_SPLINE,
  MORPH_SPLINE,
  DWELL_SPLINE,
  MORPH_SPLINE,
  DWELL_SPLINE,
  MORPH_SPLINE,
].join(';');
/*
  Base tempo in seconds at speed 1. The ratios carry the choreography: the sweep runs twice per
  morph cycle, the inner satellite laps twice per outer lap, and a twinkle is half an inner lap, so
  scaling them all by the same `speed` keeps the parts in step.
*/
const MORPH_SECONDS = 4;
const SWEEP_SECONDS = 2;
const ORBIT_CW_SECONDS = 6;
const ORBIT_CCW_SECONDS = 3;
const TWINKLE_SECONDS = 1.5;

const secs = (base: number, speed: number) => `${(base / speed).toFixed(2)}s`;

/*
  Satellite sizing. A tip reaches scale × 10.5 (worst-case mark half-extent) × 1.15 (twinkle peak)
  from its orbit point, and the half-scale centre reaches 5.25, so both satellites must sit inside
  that band:
    outer  r 8.8, s 0.26 → reach 3.14 → 11.94 out / 5.66 in
    inner  r 7.8, s 0.21 → reach 2.54 → 10.34 out / 5.26 in
*/
const OUTER_TRANSFORM = 'translate(12 12) scale(0.26) translate(-12 -12)';
const INNER_TRANSFORM = 'translate(12 12) scale(0.21) translate(-12 -12)';

/*
  Sweep colours, mirroring the tailwind --background-image-ai-gradient pairing (electric-violet into
  indigo-crush) with a lighter violet glint. BODY_STOP hands the violet most of the tile so it never
  reads as a thin tail; the 0.04 gap after the glint keeps its trailing edge crisp.
*/
const TAIL_COLOR = 'var(--color-electric-violet)';
const BODY_COLOR = 'var(--color-indigo-crush)';
const LEAD_COLOR = 'var(--color-electric-violet-80)';
const BODY_STOP = 0.6;
const LEAD_STOP = 0.78;
const BODY_STOP_OUT = 0.82;

export interface AiLoaderIconProps {
  size?: number;
  /** Tempo multiplier for every part of the animation. Above 1 is faster, below 1 slower. */
  speed?: number;
  className?: string;
}

export const AiLoaderIcon = ({ size = 32, speed = 1, className }: AiLoaderIconProps) => {
  const rawId = useId().replace(/[^a-zA-Z0-9]/g, '');
  const gradId = `aiLoaderIconG${rawId}`;
  // A zero or negative multiplier would yield infinite or negative durations and stall the animation.
  const tempo = speed > 0 ? speed : 1;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={cn('ai-loader-icon', 'text-indigo-crush', className)}
      style={
        {
          '--ai-loader-icon-orbit-cw-dur': secs(ORBIT_CW_SECONDS, tempo),
          '--ai-loader-icon-orbit-ccw-dur': secs(ORBIT_CCW_SECONDS, tempo),
          '--ai-loader-icon-twinkle-dur': secs(TWINKLE_SECONDS, tempo),
          '--ai-loader-icon-twinkle-delay': secs(-TWINKLE_SECONDS / 2, tempo),
        } as CSSProperties
      }>
      <defs>
        {/*
          gradientUnits=userSpaceOnUse + spreadMethod=repeat tiles every (0,0)→(24,24) diagonal, so
          translating gradientTransform by exactly one period (24,24) loops invisibly. First and last
          stops share the tail colour so the tile seam has no colour jump.
        */}
        <linearGradient
          id={gradId}
          gradientUnits="userSpaceOnUse"
          spreadMethod="repeat"
          x1="0"
          y1="0"
          x2="24"
          y2="24">
          <stop offset="0" stopColor={TAIL_COLOR} />
          <stop offset={BODY_STOP} stopColor={BODY_COLOR} />
          <stop offset={LEAD_STOP} stopColor={LEAD_COLOR} />
          <stop offset={BODY_STOP_OUT} stopColor={BODY_COLOR} />
          <stop offset="1" stopColor={TAIL_COLOR} />
          <animateTransform
            attributeName="gradientTransform"
            type="translate"
            from="0 0"
            to="24 24"
            dur={secs(SWEEP_SECONDS, tempo)}
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>

      {/* Centre mark at half scale, gradient-filled; a currentColor twin covers reduced motion. */}
      <g transform="translate(6 6) scale(0.5)">
        <path className={styles['animated']} fill={`url(#${gradId})`} d={MORPH_BB_SPARKLE}>
          <animate
            attributeName="d"
            dur={secs(MORPH_SECONDS, tempo)}
            repeatCount="indefinite"
            calcMode="spline"
            keyTimes={KEYTIMES}
            keySplines={SPLINES}
            values={FRAMES.join(';')}
          />
        </path>
        <path className={styles['static']} fill="currentColor" d={MORPH_BB_LOGO} />
      </g>

      {/* Outer sparkle: clockwise, radius 8.8. */}
      <g className={cn(styles['orbit'], styles['orbitCw'])}>
        <g className={cn(styles['twinkle'], styles['twinkleA'])}>
          <path d={MORPH_BB_SPARKLE} transform={OUTER_TRANSFORM} />
        </g>
      </g>

      {/* Inner sparkle: counter-clockwise, radius 7.8. */}
      <g className={cn(styles['orbit'], styles['orbitCcw'], styles['orbitFaint'])}>
        <g className={cn(styles['twinkle'], styles['twinkleB'])}>
          <path d={MORPH_BB_SPARKLE} transform={INNER_TRANSFORM} />
        </g>
      </g>
    </svg>
  );
};
